import React, { useEffect, useState, useCallback } from 'react';
import Component from '../../components/Component';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import {
    listReelScriptConversations,
    getReelScriptConversation,
} from '../../services/reelScriptAdminApi';

function formatDate(d) {
    if (!d) return '—';
    try {
        return new Date(d).toLocaleString();
    } catch {
        return String(d);
    }
}

function scopeChip(scope) {
    const map = {
        moment: '#1976d2',
        match: '#2e7d32',
        day: '#7b1fa2',
        custom: '#616161',
    };
    return (
        <Chip
            label={scope || 'unknown'}
            size="small"
            sx={{ bgcolor: map[scope] || '#616161', color: '#fff', fontWeight: 600 }}
        />
    );
}

function truncate(text, max = 80) {
    if (!text) return '';
    return text.length > max ? `${text.slice(0, max)}…` : text;
}

function MessageBubble({ message }) {
    const isUser = message.role === 'user';
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                mb: 1.5,
            }}
        >
            <Box
                sx={{
                    maxWidth: '75%',
                    bgcolor: isUser ? '#1976d2' : '#f0f0f0',
                    color: isUser ? '#fff' : 'text.primary',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 700 }}>
                        {message.role}
                    </Typography>
                    {message.type === 'script' && (
                        <Chip label="script" size="small" sx={{ height: 18, fontSize: 11 }} />
                    )}
                    {message.image_analysis && (
                        <Chip label="image" size="small" sx={{ height: 18, fontSize: 11 }} />
                    )}
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                </Typography>
                {message.image_analysis && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.75, fontStyle: 'italic' }}>
                        Image seen: {message.image_analysis}
                    </Typography>
                )}
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.6 }}>
                    {formatDate(message.created_at)}
                    {message.model ? ` · ${message.model}` : ''}
                    {message.latency_ms ? ` · ${message.latency_ms}ms` : ''}
                </Typography>
            </Box>
        </Box>
    );
}

function ReelScriptAdmin() {
    const [conversations, setConversations] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [userIdFilter, setUserIdFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listReelScriptConversations(
                rowsPerPage,
                page * rowsPerPage,
                userIdFilter.trim() || null,
            );
            setConversations(res?.data?.conversations || []);
            setTotal(res?.data?.total || 0);
        } catch (err) {
            setError(err.message || 'Failed to load conversations');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, userIdFilter]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (conversationId) => {
        setDetail({ conversation_id: conversationId });
        setDetailLoading(true);
        setDetailError('');
        try {
            const res = await getReelScriptConversation(conversationId);
            setDetail(res?.data || null);
        } catch (err) {
            setDetailError(err.message || 'Failed to load conversation');
        } finally {
            setDetailLoading(false);
        }
    };

    return (
        <Component>
            <div className="page-body">
                <div className="container-fluid">
                    <Box sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Box>
                                <Typography variant="h5" fontWeight={700}>Reel-Script Conversations</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Support/debug view over reel-script chat history — every user's threads, guests included.
                                </Typography>
                            </Box>
                            <IconButton onClick={load} disabled={loading} title="Refresh">
                                <RefreshIcon />
                            </IconButton>
                        </Stack>

                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <TextField
                                    label="Filter by user ID"
                                    size="small"
                                    value={userIdFilter}
                                    onChange={(e) => { setPage(0); setUserIdFilter(e.target.value); }}
                                    placeholder="Leave empty to show all users (incl. guests)"
                                    sx={{ minWidth: 320 }}
                                />
                            </CardContent>
                        </Card>

                        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Card>
                                <TableContainer component={Paper} elevation={0}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Conversation ID</TableCell>
                                                <TableCell>User</TableCell>
                                                <TableCell>Scope</TableCell>
                                                <TableCell>Match IDs</TableCell>
                                                <TableCell>Messages</TableCell>
                                                <TableCell>Last message</TableCell>
                                                <TableCell>Created</TableCell>
                                                <TableCell>Updated</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {conversations.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={9} align="center">
                                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                                            No conversations found.
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : conversations.map((c) => (
                                                <TableRow key={c.conversation_id} hover>
                                                    <TableCell>
                                                        <Typography variant="caption" fontFamily="monospace">
                                                            {c.conversation_id}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{c.user_id || <em>guest</em>}</TableCell>
                                                    <TableCell>{scopeChip(c.scope)}</TableCell>
                                                    <TableCell>{(c.match_ids || []).join(', ') || '—'}</TableCell>
                                                    <TableCell>{c.message_count}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption">
                                                            {c.last_message ? truncate(c.last_message.content) : '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{formatDate(c.created_at)}</TableCell>
                                                    <TableCell>{formatDate(c.updated_at)}</TableCell>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openDetail(c.conversation_id)}
                                                            title="View conversation"
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    component="div"
                                    count={total}
                                    page={page}
                                    onPageChange={(_, newPage) => setPage(newPage)}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                    rowsPerPageOptions={[10, 20, 50, 100]}
                                />
                            </Card>
                        )}
                    </Box>
                </div>
            </div>

            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>Conversation</Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                            {detail?.conversation_id}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setDetail(null)} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {detailLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : detailError ? (
                        <Alert severity="error">{detailError}</Alert>
                    ) : detail ? (
                        <>
                            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                                {scopeChip(detail.scope)}
                                <Chip label={detail.user_id ? `user: ${detail.user_id}` : 'guest'} size="small" variant="outlined" />
                                {detail.current_script_id && (
                                    <Chip label={`script: ${detail.current_script_id}`} size="small" variant="outlined" />
                                )}
                            </Stack>
                            {(detail.messages || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No messages yet.</Typography>
                            ) : (
                                (detail.messages || []).map((m, i) => (
                                    <MessageBubble key={i} message={m} />
                                ))
                            )}
                        </>
                    ) : null}
                </DialogContent>
            </Dialog>
        </Component>
    );
}

export default ReelScriptAdmin;
